import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Search as SearchIcon } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../context/I18nContext';
import BottomNav from '../components/BottomNav';
import UserAvatar from '../components/UserAvatar';
import { resolveMediaUrl } from '../lib/social';
import { formatAura } from '../lib/aura';

export default function SearchPage() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [q, setQ] = useState('');
  const [data, setData] = useState({ users: [], hashtags: [], posts: [] });

  useEffect(() => {
    if (!q.trim()) { setData({ users: [], hashtags: [], posts: [] }); return undefined; }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get('/search', { params: { q } });
        setData(data);
      } catch (_e) { /* ignore */ }
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const empty = !q.trim();
  const noResults = q.trim() && !data.users.length && !data.hashtags.length && !data.posts.length;

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative">
      <header className="flex items-center gap-2 px-4 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim p-2"><ChevronLeft size={20} /></button>
        <div className="flex-1 relative">
          <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-aura-textDim" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            autoFocus placeholder={t('search.placeholder')}
            data-testid="search-input"
            className="aura-input pl-9"
          />
        </div>
      </header>

      <main className="mx-auto max-w-md px-5 pt-4 space-y-6">
        {empty && <div className="text-center text-aura-textDim py-16">{t('search.empty')}</div>}
        {noResults && <div className="text-center text-aura-textDim py-16">{t('search.noResults')}</div>}

        {data.users?.length > 0 && (
          <section>
            <h3 className="font-display font-bold text-white text-xs tracking-widest mb-2">{t('search.users')}</h3>
            <div className="aura-card divide-y divide-white/5" data-testid="search-users">
              {data.users.map((u) => (
                <Link key={u.user_id} to={`/u/${u.username}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5">
                  <UserAvatar user={u} size="xs" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-display font-semibold truncate">{u.name}</div>
                    <div className="text-[11px] text-aura-textDim truncate">@{u.username}</div>
                  </div>
                  <div className="text-xs font-mono text-aura-cyan">{formatAura(u.aura_points || 0)}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {data.hashtags?.length > 0 && (
          <section>
            <h3 className="font-display font-bold text-white text-xs tracking-widest mb-2">{t('search.hashtags')}</h3>
            <div className="flex flex-wrap gap-2">
              {data.hashtags.map((h) => (
                <Link key={h.tag} to={`/tag/${h.tag}`} className="aura-chip !text-sm">#{h.tag} <span className="font-mono text-[10px] text-aura-textDim ml-1">{h.count}</span></Link>
              ))}
            </div>
          </section>
        )}

        {data.posts?.length > 0 && (
          <section>
            <h3 className="font-display font-bold text-white text-xs tracking-widest mb-2">{t('search.posts')}</h3>
            <div className="grid grid-cols-3 gap-1.5">
              {data.posts.map((p) => (
                <Link key={p.post_id} to={`/post/${p.public_post_id}`} className="aspect-[9/16] relative overflow-hidden rounded-lg bg-aura-surface2 border border-white/5">
                  {p.media?.[0] ? (
                    <img src={resolveMediaUrl(p.media[0].thumbnail_url || p.media[0].url)} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center px-2 text-xs text-white/80 text-center">{(p.text_body || p.caption || '').slice(0, 60)}</div>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
